// Multi-language translations for the Decrepalma website.
// Keys are shared across sections; PT is source-of-truth.

export const LANGUAGES = [
  { code: "pt", label: "PT", name: "Português" },
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
];

export const translations = {
  pt: {
    nav: {
      services: "Serviços",
      about: "Sobre",
      contact: "Contactos",
      cta: "Marcar Consulta",
      langLabel: "Idioma",
    },
    hero: {
      eyebrow: "Gabinete de Contabilidade — Desde 1998",
      title: ["Rigor contabilístico,", "decisões", "com confiança."],
      lede: "Assessoria fiscal, contabilística e de gestão para empresas, empresários e particulares que exigem clareza, cumprimento e resultados.",
      primaryCta: "Marcar Consulta",
      secondaryCta: "Ver serviços",
      stat1Value: "27",
      stat1Label: "Anos de experiência",
      stat2Value: "400+",
      stat2Label: "Clientes ativos",
      stat3Value: "100%",
      stat3Label: "Cumprimento fiscal",
    },
    services: {
      eyebrow: "Serviços",
      title: "Um parceiro completo para a sua contabilidade.",
      lede: "Do lançamento diário à consultoria estratégica — cobrimos todo o ciclo fiscal e contabilístico do seu negócio.",
      items: [
        {
          key: "01",
          title: "Contabilidade Geral",
          body: "Organização e classificação documental, lançamentos, apuramentos e reporting mensal em conformidade com o SNC.",
        },
        {
          key: "02",
          title: "IRS & IRC",
          body: "Preparação e submissão de declarações fiscais para empresas e empresários, otimizando enquadramento e benefícios.",
        },
        {
          key: "03",
          title: "Processamento Salarial",
          body: "Cálculo, recibos, SS, IRS retido e comunicações à AT e à Segurança Social — sem atrasos, sem coimas.",
        },
        {
          key: "04",
          title: "Consultoria Fiscal",
          body: "Planeamento fiscal, apoio em inspeções, constituição de empresas e análise de rentabilidade.",
        },
      ],
    },
    about: {
      eyebrow: "Sobre a Decrepalma",
      title: "Números claros. Decisões seguras.",
      p1: "A Decrepalma é um gabinete de contabilidade certificado que combina rigor técnico com uma abordagem próxima e humana. Trabalhamos com empresas, empresários e particulares que valorizam transparência e resposta rápida.",
      p2: "Acompanhamos cada cliente como se fosse o único — porque as suas decisões financeiras merecem tempo, atenção e verdade.",
      points: [
        "Contabilista Certificado inscrito na OCC",
        "Software homologado pela Autoridade Tributária",
        "Atendimento presencial e digital",
        "Sigilo profissional garantido",
      ],
    },
    contact: {
      eyebrow: "Contactos",
      title: "Vamos conversar sobre o seu negócio.",
      lede: "Envie-nos uma mensagem. Respondemos em menos de 24h úteis.",
      fields: {
        name: "Nome",
        email: "Email",
        phone: "Telefone",
        subject: "Assunto",
        message: "Mensagem",
      },
      placeholders: {
        name: "O seu nome",
        email: "email@exemplo.pt",
        phone: "+351 000 000 000",
        subject: "Como podemos ajudar?",
        message: "Descreva a sua situação...",
      },
      submit: "Enviar mensagem",
      submitting: "A enviar...",
      success: "Mensagem enviada. Entraremos em contacto em breve.",
      error: "Não foi possível enviar. Tente novamente ou contacte-nos diretamente.",
      infoTitle: "Informações",
      email: "geral@decrepalma.pt",
      phone: "+351 281 249 911",
      whatsapp: "+351 968 343 594",
      whatsappLabel: "WhatsApp",
      hours: "Seg–Sex · 09:00–18:00",
      address: "Portugal",
      mapLabel: "Ver no mapa",
      directions: "Como chegar",
    },
    footer: {
      tagline: "Rigor contabilístico ao serviço do seu negócio.",
      rights: "Todos os direitos reservados.",
      links: { services: "Serviços", about: "Sobre", contact: "Contactos" },
    },
    seo: {
      title: "Decrepalma — Contabilidade, Fiscalidade e Consultoria",
      description:
        "Gabinete de contabilidade certificado em Portugal. Contabilidade geral, IRS, IRC, salários e consultoria fiscal para empresas, empresários e particulares.",
    },
  },

  en: {
    nav: {
      services: "Services",
      about: "About",
      contact: "Contact",
      cta: "Book a call",
      langLabel: "Language",
    },
    hero: {
      eyebrow: "Chartered Accounting Firm — Since 1998",
      title: ["Accounting precision,", "decisions", "you can trust."],
      lede: "Tax, accounting and management advisory for companies, entrepreneurs and individuals who demand clarity, compliance and results.",
      primaryCta: "Book a call",
      secondaryCta: "See services",
      stat1Value: "27",
      stat1Label: "Years of experience",
      stat2Value: "400+",
      stat2Label: "Active clients",
      stat3Value: "100%",
      stat3Label: "Tax compliance",
    },
    services: {
      eyebrow: "Services",
      title: "A complete partner for your accounting.",
      lede: "From day-to-day bookkeeping to strategic advisory — we cover the full tax and accounting cycle of your business.",
      items: [
        {
          key: "01",
          title: "General Accounting",
          body: "Document organisation, bookkeeping, monthly reporting and reconciliations under the Portuguese accounting standard (SNC).",
        },
        {
          key: "02",
          title: "Personal & Corporate Tax",
          body: "Preparation and filing of tax returns for companies and entrepreneurs — optimising your tax framework and benefits.",
        },
        {
          key: "03",
          title: "Payroll",
          body: "Salary calculation, payslips, social security and withholding tax filings — on time, every time.",
        },
        {
          key: "04",
          title: "Tax Advisory",
          body: "Tax planning, audit support, company incorporation and profitability analysis.",
        },
      ],
    },
    about: {
      eyebrow: "About Decrepalma",
      title: "Clear numbers. Confident decisions.",
      p1: "Decrepalma is a certified accounting firm that pairs technical rigour with a close, human approach. We work with companies, entrepreneurs and individuals who value transparency and quick responses.",
      p2: "We handle every client as if they were the only one — because your financial decisions deserve time, attention and honesty.",
      points: [
        "Certified Accountant registered at OCC",
        "Software approved by the Portuguese Tax Authority",
        "In-person and digital service",
        "Guaranteed professional confidentiality",
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk about your business.",
      lede: "Send us a message. We reply within 24 business hours.",
      fields: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        subject: "Subject",
        message: "Message",
      },
      placeholders: {
        name: "Your name",
        email: "email@example.com",
        phone: "+351 000 000 000",
        subject: "How can we help?",
        message: "Describe your situation...",
      },
      submit: "Send message",
      submitting: "Sending...",
      success: "Message sent. We'll be in touch shortly.",
      error: "We couldn't send your message. Please try again or reach us directly.",
      infoTitle: "Information",
      email: "geral@decrepalma.pt",
      phone: "+351 281 249 911",
      whatsapp: "+351 968 343 594",
      whatsappLabel: "WhatsApp",
      hours: "Mon–Fri · 09:00–18:00",
      address: "Portugal",
      mapLabel: "View on map",
      directions: "Directions",
    },
    footer: {
      tagline: "Accounting precision at the service of your business.",
      rights: "All rights reserved.",
      links: { services: "Services", about: "About", contact: "Contact" },
    },
    seo: {
      title: "Decrepalma — Accounting, Tax & Advisory",
      description:
        "Certified accounting firm in Portugal. General accounting, personal & corporate tax, payroll and tax advisory for companies, entrepreneurs and individuals.",
    },
  },

  es: {
    nav: {
      services: "Servicios",
      about: "Sobre",
      contact: "Contacto",
      cta: "Agendar cita",
      langLabel: "Idioma",
    },
    hero: {
      eyebrow: "Despacho Contable — Desde 1998",
      title: ["Rigor contable,", "decisiones", "con confianza."],
      lede: "Asesoría fiscal, contable y de gestión para empresas, empresarios y particulares que exigen claridad, cumplimiento y resultados.",
      primaryCta: "Agendar cita",
      secondaryCta: "Ver servicios",
      stat1Value: "27",
      stat1Label: "Años de experiencia",
      stat2Value: "400+",
      stat2Label: "Clientes activos",
      stat3Value: "100%",
      stat3Label: "Cumplimiento fiscal",
    },
    services: {
      eyebrow: "Servicios",
      title: "Un socio completo para su contabilidad.",
      lede: "Desde la contabilidad diaria hasta la consultoría estratégica — cubrimos todo el ciclo fiscal y contable de su negocio.",
      items: [
        {
          key: "01",
          title: "Contabilidad General",
          body: "Organización documental, asientos, cierres y reportes mensuales conforme al SNC portugués.",
        },
        {
          key: "02",
          title: "IRS e IRC",
          body: "Preparación y presentación de declaraciones fiscales para empresas y empresarios, optimizando el marco fiscal y beneficios.",
        },
        {
          key: "03",
          title: "Nóminas",
          body: "Cálculo, recibos, Seguridad Social y retenciones — sin retrasos ni sanciones.",
        },
        {
          key: "04",
          title: "Consultoría Fiscal",
          body: "Planificación fiscal, apoyo en inspecciones, constitución de empresas y análisis de rentabilidad.",
        },
      ],
    },
    about: {
      eyebrow: "Sobre Decrepalma",
      title: "Números claros. Decisiones seguras.",
      p1: "Decrepalma es un despacho contable certificado que combina rigor técnico con un trato cercano y humano. Trabajamos con empresas, empresarios y particulares que valoran la transparencia y la respuesta rápida.",
      p2: "Tratamos a cada cliente como si fuera único — porque sus decisiones financieras merecen tiempo, atención y honestidad.",
      points: [
        "Contable certificado inscrito en la OCC",
        "Software homologado por la Autoridad Tributaria",
        "Atención presencial y digital",
        "Confidencialidad profesional garantizada",
      ],
    },
    contact: {
      eyebrow: "Contacto",
      title: "Hablemos de su negocio.",
      lede: "Envíenos un mensaje. Respondemos en menos de 24h laborables.",
      fields: {
        name: "Nombre",
        email: "Email",
        phone: "Teléfono",
        subject: "Asunto",
        message: "Mensaje",
      },
      placeholders: {
        name: "Su nombre",
        email: "email@ejemplo.com",
        phone: "+351 000 000 000",
        subject: "¿Cómo podemos ayudar?",
        message: "Describa su situación...",
      },
      submit: "Enviar mensaje",
      submitting: "Enviando...",
      success: "Mensaje enviado. Le contactaremos en breve.",
      error: "No fue posible enviar. Inténtelo de nuevo o contáctenos directamente.",
      infoTitle: "Información",
      email: "geral@decrepalma.pt",
      phone: "+351 281 249 911",
      whatsapp: "+351 968 343 594",
      whatsappLabel: "WhatsApp",
      hours: "Lun–Vie · 09:00–18:00",
      address: "Portugal",
      mapLabel: "Ver en el mapa",
      directions: "Cómo llegar",
    },
    footer: {
      tagline: "Rigor contable al servicio de su negocio.",
      rights: "Todos los derechos reservados.",
      links: { services: "Servicios", about: "Sobre", contact: "Contacto" },
    },
    seo: {
      title: "Decrepalma — Contabilidad, Fiscalidad y Consultoría",
      description:
        "Despacho contable certificado en Portugal. Contabilidad general, IRS, IRC, nóminas y consultoría fiscal para empresas, empresarios y particulares.",
    },
  },
};

export const getT = (lang) => translations[lang] || translations.pt;

// Business constants
export const CONTACT_INFO = {
  email: "geral@decrepalma.pt",
  phone: "+351 281 249 911",
  phoneRaw: "+351281249911",
  whatsapp: "+351 968 343 594",
  whatsappRaw: "351968343594",
  coords: { lat: 37.194211, lng: -7.424719 },
};
